import {
    Cell,
    Slice,
    Address,
    Builder,
    beginCell,
    ComputeError,
    TupleItem,
    TupleReader,
    Dictionary,
    contractAddress,
    address,
    ContractProvider,
    Sender,
    Contract,
    ContractABI,
    ABIType,
    ABIGetter,
    ABIReceiver,
    TupleBuilder,
    DictionaryValue
} from '@ton/core';

export type DataSize = {
    $$type: 'DataSize';
    cells: bigint;
    bits: bigint;
    refs: bigint;
}

export function storeDataSize(src: DataSize) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.cells, 257);
        b_0.storeInt(src.bits, 257);
        b_0.storeInt(src.refs, 257);
    };
}

export function loadDataSize(slice: Slice) {
    const sc_0 = slice;
    const _cells = sc_0.loadIntBig(257);
    const _bits = sc_0.loadIntBig(257);
    const _refs = sc_0.loadIntBig(257);
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function loadTupleDataSize(source: TupleReader) {
    const _cells = source.readBigNumber();
    const _bits = source.readBigNumber();
    const _refs = source.readBigNumber();
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function loadGetterTupleDataSize(source: TupleReader) {
    const _cells = source.readBigNumber();
    const _bits = source.readBigNumber();
    const _refs = source.readBigNumber();
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function storeTupleDataSize(source: DataSize) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.cells);
    builder.writeNumber(source.bits);
    builder.writeNumber(source.refs);
    return builder.build();
}

export function dictValueParserDataSize(): DictionaryValue<DataSize> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDataSize(src)).endCell());
        },
        parse: (src) => {
            return loadDataSize(src.loadRef().beginParse());
        }
    }
}

export type SignedBundle = {
    $$type: 'SignedBundle';
    signature: Buffer;
    signedData: Slice;
}

export function storeSignedBundle(src: SignedBundle) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBuffer(src.signature);
        b_0.storeBuilder(src.signedData.asBuilder());
    };
}

export function loadSignedBundle(slice: Slice) {
    const sc_0 = slice;
    const _signature = sc_0.loadBuffer(64);
    const _signedData = sc_0;
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function loadTupleSignedBundle(source: TupleReader) {
    const _signature = source.readBuffer();
    const _signedData = source.readCell().asSlice();
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function loadGetterTupleSignedBundle(source: TupleReader) {
    const _signature = source.readBuffer();
    const _signedData = source.readCell().asSlice();
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function storeTupleSignedBundle(source: SignedBundle) {
    const builder = new TupleBuilder();
    builder.writeBuffer(source.signature);
    builder.writeSlice(source.signedData.asCell());
    return builder.build();
}

export function dictValueParserSignedBundle(): DictionaryValue<SignedBundle> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeSignedBundle(src)).endCell());
        },
        parse: (src) => {
            return loadSignedBundle(src.loadRef().beginParse());
        }
    }
}

export type StateInit = {
    $$type: 'StateInit';
    code: Cell;
    data: Cell;
}

export function storeStateInit(src: StateInit) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeRef(src.code);
        b_0.storeRef(src.data);
    };
}

export function loadStateInit(slice: Slice) {
    const sc_0 = slice;
    const _code = sc_0.loadRef();
    const _data = sc_0.loadRef();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function loadTupleStateInit(source: TupleReader) {
    const _code = source.readCell();
    const _data = source.readCell();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function loadGetterTupleStateInit(source: TupleReader) {
    const _code = source.readCell();
    const _data = source.readCell();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function storeTupleStateInit(source: StateInit) {
    const builder = new TupleBuilder();
    builder.writeCell(source.code);
    builder.writeCell(source.data);
    return builder.build();
}

export function dictValueParserStateInit(): DictionaryValue<StateInit> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeStateInit(src)).endCell());
        },
        parse: (src) => {
            return loadStateInit(src.loadRef().beginParse());
        }
    }
}

export type Context = {
    $$type: 'Context';
    bounceable: boolean;
    sender: Address;
    value: bigint;
    raw: Slice;
}

export function storeContext(src: Context) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.bounceable);
        b_0.storeAddress(src.sender);
        b_0.storeInt(src.value, 257);
        b_0.storeRef(src.raw.asCell());
    };
}

export function loadContext(slice: Slice) {
    const sc_0 = slice;
    const _bounceable = sc_0.loadBit();
    const _sender = sc_0.loadAddress();
    const _value = sc_0.loadIntBig(257);
    const _raw = sc_0.loadRef().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function loadTupleContext(source: TupleReader) {
    const _bounceable = source.readBoolean();
    const _sender = source.readAddress();
    const _value = source.readBigNumber();
    const _raw = source.readCell().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function loadGetterTupleContext(source: TupleReader) {
    const _bounceable = source.readBoolean();
    const _sender = source.readAddress();
    const _value = source.readBigNumber();
    const _raw = source.readCell().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function storeTupleContext(source: Context) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.bounceable);
    builder.writeAddress(source.sender);
    builder.writeNumber(source.value);
    builder.writeSlice(source.raw.asCell());
    return builder.build();
}

export function dictValueParserContext(): DictionaryValue<Context> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeContext(src)).endCell());
        },
        parse: (src) => {
            return loadContext(src.loadRef().beginParse());
        }
    }
}

export type SendParameters = {
    $$type: 'SendParameters';
    mode: bigint;
    body: Cell | null;
    code: Cell | null;
    data: Cell | null;
    value: bigint;
    to: Address;
    bounce: boolean;
}

export function storeSendParameters(src: SendParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        if (src.code !== null && src.code !== undefined) { b_0.storeBit(true).storeRef(src.code); } else { b_0.storeBit(false); }
        if (src.data !== null && src.data !== undefined) { b_0.storeBit(true).storeRef(src.data); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeAddress(src.to);
        b_0.storeBit(src.bounce);
    };
}

export function loadSendParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _code = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _data = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _to = sc_0.loadAddress();
    const _bounce = sc_0.loadBit();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function loadTupleSendParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _code = source.readCellOpt();
    const _data = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function loadGetterTupleSendParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _code = source.readCellOpt();
    const _data = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function storeTupleSendParameters(source: SendParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeCell(source.code);
    builder.writeCell(source.data);
    builder.writeNumber(source.value);
    builder.writeAddress(source.to);
    builder.writeBoolean(source.bounce);
    return builder.build();
}

export function dictValueParserSendParameters(): DictionaryValue<SendParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeSendParameters(src)).endCell());
        },
        parse: (src) => {
            return loadSendParameters(src.loadRef().beginParse());
        }
    }
}

export type MessageParameters = {
    $$type: 'MessageParameters';
    mode: bigint;
    body: Cell | null;
    value: bigint;
    to: Address;
    bounce: boolean;
}

export function storeMessageParameters(src: MessageParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeAddress(src.to);
        b_0.storeBit(src.bounce);
    };
}

export function loadMessageParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _to = sc_0.loadAddress();
    const _bounce = sc_0.loadBit();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function loadTupleMessageParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function loadGetterTupleMessageParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function storeTupleMessageParameters(source: MessageParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeNumber(source.value);
    builder.writeAddress(source.to);
    builder.writeBoolean(source.bounce);
    return builder.build();
}

export function dictValueParserMessageParameters(): DictionaryValue<MessageParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMessageParameters(src)).endCell());
        },
        parse: (src) => {
            return loadMessageParameters(src.loadRef().beginParse());
        }
    }
}

export type DeployParameters = {
    $$type: 'DeployParameters';
    mode: bigint;
    body: Cell | null;
    value: bigint;
    bounce: boolean;
    init: StateInit;
}

export function storeDeployParameters(src: DeployParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeBit(src.bounce);
        b_0.store(storeStateInit(src.init));
    };
}

export function loadDeployParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _bounce = sc_0.loadBit();
    const _init = loadStateInit(sc_0);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function loadTupleDeployParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _bounce = source.readBoolean();
    const _init = loadTupleStateInit(source);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function loadGetterTupleDeployParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _bounce = source.readBoolean();
    const _init = loadGetterTupleStateInit(source);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function storeTupleDeployParameters(source: DeployParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeNumber(source.value);
    builder.writeBoolean(source.bounce);
    builder.writeTuple(storeTupleStateInit(source.init));
    return builder.build();
}

export function dictValueParserDeployParameters(): DictionaryValue<DeployParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeployParameters(src)).endCell());
        },
        parse: (src) => {
            return loadDeployParameters(src.loadRef().beginParse());
        }
    }
}

export type StdAddress = {
    $$type: 'StdAddress';
    workchain: bigint;
    address: bigint;
}

export function storeStdAddress(src: StdAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.workchain, 8);
        b_0.storeUint(src.address, 256);
    };
}

export function loadStdAddress(slice: Slice) {
    const sc_0 = slice;
    const _workchain = sc_0.loadIntBig(8);
    const _address = sc_0.loadUintBig(256);
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function loadTupleStdAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readBigNumber();
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function loadGetterTupleStdAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readBigNumber();
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function storeTupleStdAddress(source: StdAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.workchain);
    builder.writeNumber(source.address);
    return builder.build();
}

export function dictValueParserStdAddress(): DictionaryValue<StdAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeStdAddress(src)).endCell());
        },
        parse: (src) => {
            return loadStdAddress(src.loadRef().beginParse());
        }
    }
}

export type VarAddress = {
    $$type: 'VarAddress';
    workchain: bigint;
    address: Slice;
}

export function storeVarAddress(src: VarAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.workchain, 32);
        b_0.storeRef(src.address.asCell());
    };
}

export function loadVarAddress(slice: Slice) {
    const sc_0 = slice;
    const _workchain = sc_0.loadIntBig(32);
    const _address = sc_0.loadRef().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function loadTupleVarAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readCell().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function loadGetterTupleVarAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readCell().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function storeTupleVarAddress(source: VarAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.workchain);
    builder.writeSlice(source.address.asCell());
    return builder.build();
}

export function dictValueParserVarAddress(): DictionaryValue<VarAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeVarAddress(src)).endCell());
        },
        parse: (src) => {
            return loadVarAddress(src.loadRef().beginParse());
        }
    }
}

export type BasechainAddress = {
    $$type: 'BasechainAddress';
    hash: bigint | null;
}

export function storeBasechainAddress(src: BasechainAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        if (src.hash !== null && src.hash !== undefined) { b_0.storeBit(true).storeInt(src.hash, 257); } else { b_0.storeBit(false); }
    };
}

export function loadBasechainAddress(slice: Slice) {
    const sc_0 = slice;
    const _hash = sc_0.loadBit() ? sc_0.loadIntBig(257) : null;
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function loadTupleBasechainAddress(source: TupleReader) {
    const _hash = source.readBigNumberOpt();
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function loadGetterTupleBasechainAddress(source: TupleReader) {
    const _hash = source.readBigNumberOpt();
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function storeTupleBasechainAddress(source: BasechainAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.hash);
    return builder.build();
}

export function dictValueParserBasechainAddress(): DictionaryValue<BasechainAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBasechainAddress(src)).endCell());
        },
        parse: (src) => {
            return loadBasechainAddress(src.loadRef().beginParse());
        }
    }
}

export type Deploy = {
    $$type: 'Deploy';
    queryId: bigint;
}

export function storeDeploy(src: Deploy) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2490013878, 32);
        b_0.storeUint(src.queryId, 64);
    };
}

export function loadDeploy(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2490013878) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function loadTupleDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function loadGetterTupleDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function storeTupleDeploy(source: Deploy) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    return builder.build();
}

export function dictValueParserDeploy(): DictionaryValue<Deploy> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeploy(src)).endCell());
        },
        parse: (src) => {
            return loadDeploy(src.loadRef().beginParse());
        }
    }
}

export type DeployOk = {
    $$type: 'DeployOk';
    queryId: bigint;
}

export function storeDeployOk(src: DeployOk) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2952335191, 32);
        b_0.storeUint(src.queryId, 64);
    };
}

export function loadDeployOk(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2952335191) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function loadTupleDeployOk(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function loadGetterTupleDeployOk(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function storeTupleDeployOk(source: DeployOk) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    return builder.build();
}

export function dictValueParserDeployOk(): DictionaryValue<DeployOk> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeployOk(src)).endCell());
        },
        parse: (src) => {
            return loadDeployOk(src.loadRef().beginParse());
        }
    }
}

export type FactoryDeploy = {
    $$type: 'FactoryDeploy';
    queryId: bigint;
    cashback: Address;
}

export function storeFactoryDeploy(src: FactoryDeploy) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1829761339, 32);
        b_0.storeUint(src.queryId, 64);
        b_0.storeAddress(src.cashback);
    };
}

export function loadFactoryDeploy(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1829761339) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    const _cashback = sc_0.loadAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function loadTupleFactoryDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    const _cashback = source.readAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function loadGetterTupleFactoryDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    const _cashback = source.readAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function storeTupleFactoryDeploy(source: FactoryDeploy) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    builder.writeAddress(source.cashback);
    return builder.build();
}

export function dictValueParserFactoryDeploy(): DictionaryValue<FactoryDeploy> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeFactoryDeploy(src)).endCell());
        },
        parse: (src) => {
            return loadFactoryDeploy(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetBasePrice = {
    $$type: 'OwnerSetBasePrice';
    newPrice: bigint;
}

export function storeOwnerSetBasePrice(src: OwnerSetBasePrice) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1268395613, 32);
        b_0.storeInt(src.newPrice, 257);
    };
}

export function loadOwnerSetBasePrice(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1268395613) { throw Error('Invalid prefix'); }
    const _newPrice = sc_0.loadIntBig(257);
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function loadTupleOwnerSetBasePrice(source: TupleReader) {
    const _newPrice = source.readBigNumber();
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function loadGetterTupleOwnerSetBasePrice(source: TupleReader) {
    const _newPrice = source.readBigNumber();
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function storeTupleOwnerSetBasePrice(source: OwnerSetBasePrice) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.newPrice);
    return builder.build();
}

export function dictValueParserOwnerSetBasePrice(): DictionaryValue<OwnerSetBasePrice> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetBasePrice(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetBasePrice(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetLevelMultiplier = {
    $$type: 'OwnerSetLevelMultiplier';
    level: bigint;
    k: bigint;
}

export function storeOwnerSetLevelMultiplier(src: OwnerSetLevelMultiplier) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2399467553, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.k, 257);
    };
}

export function loadOwnerSetLevelMultiplier(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2399467553) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _k = sc_0.loadIntBig(257);
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function loadTupleOwnerSetLevelMultiplier(source: TupleReader) {
    const _level = source.readBigNumber();
    const _k = source.readBigNumber();
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function loadGetterTupleOwnerSetLevelMultiplier(source: TupleReader) {
    const _level = source.readBigNumber();
    const _k = source.readBigNumber();
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function storeTupleOwnerSetLevelMultiplier(source: OwnerSetLevelMultiplier) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.k);
    return builder.build();
}

export function dictValueParserOwnerSetLevelMultiplier(): DictionaryValue<OwnerSetLevelMultiplier> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetLevelMultiplier(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetLevelMultiplier(src.loadRef().beginParse());
        }
    }
}

export type OwnerWithdraw = {
    $$type: 'OwnerWithdraw';
    amount: bigint;
    recipient: Address;
}

export function storeOwnerWithdraw(src: OwnerWithdraw) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2082387357, 32);
        b_0.storeInt(src.amount, 257);
        b_0.storeAddress(src.recipient);
    };
}

export function loadOwnerWithdraw(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2082387357) { throw Error('Invalid prefix'); }
    const _amount = sc_0.loadIntBig(257);
    const _recipient = sc_0.loadAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function loadTupleOwnerWithdraw(source: TupleReader) {
    const _amount = source.readBigNumber();
    const _recipient = source.readAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function loadGetterTupleOwnerWithdraw(source: TupleReader) {
    const _amount = source.readBigNumber();
    const _recipient = source.readAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function storeTupleOwnerWithdraw(source: OwnerWithdraw) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.amount);
    builder.writeAddress(source.recipient);
    return builder.build();
}

export function dictValueParserOwnerWithdraw(): DictionaryValue<OwnerWithdraw> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerWithdraw(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerWithdraw(src.loadRef().beginParse());
        }
    }
}

export type OwnerTransferOwnership = {
    $$type: 'OwnerTransferOwnership';
    newOwner: Address;
}

export function storeOwnerTransferOwnership(src: OwnerTransferOwnership) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2073089375, 32);
        b_0.storeAddress(src.newOwner);
    };
}

export function loadOwnerTransferOwnership(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2073089375) { throw Error('Invalid prefix'); }
    const _newOwner = sc_0.loadAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function loadTupleOwnerTransferOwnership(source: TupleReader) {
    const _newOwner = source.readAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function loadGetterTupleOwnerTransferOwnership(source: TupleReader) {
    const _newOwner = source.readAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function storeTupleOwnerTransferOwnership(source: OwnerTransferOwnership) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.newOwner);
    return builder.build();
}

export function dictValueParserOwnerTransferOwnership(): DictionaryValue<OwnerTransferOwnership> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerTransferOwnership(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerTransferOwnership(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetAdmin = {
    $$type: 'OwnerSetAdmin';
    newAdmin: Address;
}

export function storeOwnerSetAdmin(src: OwnerSetAdmin) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1015550025, 32);
        b_0.storeAddress(src.newAdmin);
    };
}

export function loadOwnerSetAdmin(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1015550025) { throw Error('Invalid prefix'); }
    const _newAdmin = sc_0.loadAddress();
    return { $$type: 'OwnerSetAdmin' as const, newAdmin: _newAdmin };
}

export function loadTupleOwnerSetAdmin(source: TupleReader) {
    const _newAdmin = source.readAddress();
    return { $$type: 'OwnerSetAdmin' as const, newAdmin: _newAdmin };
}

export function loadGetterTupleOwnerSetAdmin(source: TupleReader) {
    const _newAdmin = source.readAddress();
    return { $$type: 'OwnerSetAdmin' as const, newAdmin: _newAdmin };
}

export function storeTupleOwnerSetAdmin(source: OwnerSetAdmin) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.newAdmin);
    return builder.build();
}

export function dictValueParserOwnerSetAdmin(): DictionaryValue<OwnerSetAdmin> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetAdmin(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetAdmin(src.loadRef().beginParse());
        }
    }
}

export type OwnerActivateMega = {
    $$type: 'OwnerActivateMega';
    ticketCount: bigint;
    prizeAmount: bigint;
}

export function storeOwnerActivateMega(src: OwnerActivateMega) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2107791225, 32);
        b_0.storeInt(src.ticketCount, 257);
        b_0.storeInt(src.prizeAmount, 257);
    };
}

export function loadOwnerActivateMega(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2107791225) { throw Error('Invalid prefix'); }
    const _ticketCount = sc_0.loadIntBig(257);
    const _prizeAmount = sc_0.loadIntBig(257);
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function loadTupleOwnerActivateMega(source: TupleReader) {
    const _ticketCount = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function loadGetterTupleOwnerActivateMega(source: TupleReader) {
    const _ticketCount = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function storeTupleOwnerActivateMega(source: OwnerActivateMega) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.ticketCount);
    builder.writeNumber(source.prizeAmount);
    return builder.build();
}

export function dictValueParserOwnerActivateMega(): DictionaryValue<OwnerActivateMega> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerActivateMega(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerActivateMega(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetMegaConfig = {
    $$type: 'OwnerSetMegaConfig';
    thresholdMultiplier: bigint;
    ticketPriceMultiplier: bigint;
    defaultTicketCount: bigint;
}

export function storeOwnerSetMegaConfig(src: OwnerSetMegaConfig) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1091523188, 32);
        b_0.storeInt(src.thresholdMultiplier, 257);
        b_0.storeInt(src.ticketPriceMultiplier, 257);
        b_0.storeInt(src.defaultTicketCount, 257);
    };
}

export function loadOwnerSetMegaConfig(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1091523188) { throw Error('Invalid prefix'); }
    const _thresholdMultiplier = sc_0.loadIntBig(257);
    const _ticketPriceMultiplier = sc_0.loadIntBig(257);
    const _defaultTicketCount = sc_0.loadIntBig(257);
    return { $$type: 'OwnerSetMegaConfig' as const, thresholdMultiplier: _thresholdMultiplier, ticketPriceMultiplier: _ticketPriceMultiplier, defaultTicketCount: _defaultTicketCount };
}

export function loadTupleOwnerSetMegaConfig(source: TupleReader) {
    const _thresholdMultiplier = source.readBigNumber();
    const _ticketPriceMultiplier = source.readBigNumber();
    const _defaultTicketCount = source.readBigNumber();
    return { $$type: 'OwnerSetMegaConfig' as const, thresholdMultiplier: _thresholdMultiplier, ticketPriceMultiplier: _ticketPriceMultiplier, defaultTicketCount: _defaultTicketCount };
}

export function loadGetterTupleOwnerSetMegaConfig(source: TupleReader) {
    const _thresholdMultiplier = source.readBigNumber();
    const _ticketPriceMultiplier = source.readBigNumber();
    const _defaultTicketCount = source.readBigNumber();
    return { $$type: 'OwnerSetMegaConfig' as const, thresholdMultiplier: _thresholdMultiplier, ticketPriceMultiplier: _ticketPriceMultiplier, defaultTicketCount: _defaultTicketCount };
}

export function storeTupleOwnerSetMegaConfig(source: OwnerSetMegaConfig) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.thresholdMultiplier);
    builder.writeNumber(source.ticketPriceMultiplier);
    builder.writeNumber(source.defaultTicketCount);
    return builder.build();
}

export function dictValueParserOwnerSetMegaConfig(): DictionaryValue<OwnerSetMegaConfig> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetMegaConfig(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetMegaConfig(src.loadRef().beginParse());
        }
    }
}

export type ExecuteDrawBatch = {
    $$type: 'ExecuteDrawBatch';
    level: bigint;
}

export function storeExecuteDrawBatch(src: ExecuteDrawBatch) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2969230544, 32);
        b_0.storeInt(src.level, 257);
    };
}

export function loadExecuteDrawBatch(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2969230544) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    return { $$type: 'ExecuteDrawBatch' as const, level: _level };
}

export function loadTupleExecuteDrawBatch(source: TupleReader) {
    const _level = source.readBigNumber();
    return { $$type: 'ExecuteDrawBatch' as const, level: _level };
}

export function loadGetterTupleExecuteDrawBatch(source: TupleReader) {
    const _level = source.readBigNumber();
    return { $$type: 'ExecuteDrawBatch' as const, level: _level };
}

export function storeTupleExecuteDrawBatch(source: ExecuteDrawBatch) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    return builder.build();
}

export function dictValueParserExecuteDrawBatch(): DictionaryValue<ExecuteDrawBatch> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeExecuteDrawBatch(src)).endCell());
        },
        parse: (src) => {
            return loadExecuteDrawBatch(src.loadRef().beginParse());
        }
    }
}

export type LevelConfig = {
    $$type: 'LevelConfig';
    k: bigint;
}

export function storeLevelConfig(src: LevelConfig) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.k, 257);
    };
}

export function loadLevelConfig(slice: Slice) {
    const sc_0 = slice;
    const _k = sc_0.loadIntBig(257);
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function loadTupleLevelConfig(source: TupleReader) {
    const _k = source.readBigNumber();
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function loadGetterTupleLevelConfig(source: TupleReader) {
    const _k = source.readBigNumber();
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function storeTupleLevelConfig(source: LevelConfig) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.k);
    return builder.build();
}

export function dictValueParserLevelConfig(): DictionaryValue<LevelConfig> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeLevelConfig(src)).endCell());
        },
        parse: (src) => {
            return loadLevelConfig(src.loadRef().beginParse());
        }
    }
}

export type Cycle = {
    $$type: 'Cycle';
    id: bigint;
    ticketsSold: bigint;
    isFinished: boolean;
    nextTicket: bigint;
    tickets: Dictionary<bigint, Address>;
}

export function storeCycle(src: Cycle) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.id, 257);
        b_0.storeInt(src.ticketsSold, 257);
        b_0.storeBit(src.isFinished);
        b_0.storeInt(src.nextTicket, 257);
        b_0.storeDict(src.tickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address());
    };
}

export function loadCycle(slice: Slice) {
    const sc_0 = slice;
    const _id = sc_0.loadIntBig(257);
    const _ticketsSold = sc_0.loadIntBig(257);
    const _isFinished = sc_0.loadBit();
    const _nextTicket = sc_0.loadIntBig(257);
    const _tickets = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), sc_0);
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, nextTicket: _nextTicket, tickets: _tickets };
}

export function loadTupleCycle(source: TupleReader) {
    const _id = source.readBigNumber();
    const _ticketsSold = source.readBigNumber();
    const _isFinished = source.readBoolean();
    const _nextTicket = source.readBigNumber();
    const _tickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, nextTicket: _nextTicket, tickets: _tickets };
}

export function loadGetterTupleCycle(source: TupleReader) {
    const _id = source.readBigNumber();
    const _ticketsSold = source.readBigNumber();
    const _isFinished = source.readBoolean();
    const _nextTicket = source.readBigNumber();
    const _tickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, nextTicket: _nextTicket, tickets: _tickets };
}

export function storeTupleCycle(source: Cycle) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.id);
    builder.writeNumber(source.ticketsSold);
    builder.writeBoolean(source.isFinished);
    builder.writeNumber(source.nextTicket);
    builder.writeCell(source.tickets.size > 0 ? beginCell().storeDictDirect(source.tickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address()).endCell() : null);
    return builder.build();
}

export function dictValueParserCycle(): DictionaryValue<Cycle> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeCycle(src)).endCell());
        },
        parse: (src) => {
            return loadCycle(src.loadRef().beginParse());
        }
    }
}

export type PendingDraw = {
    $$type: 'PendingDraw';
    cycleId: bigint;
    seed: bigint;
    batchIndex: bigint;
    ticketPrice: bigint;
}

export function storePendingDraw(src: PendingDraw) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.cycleId, 257);
        b_0.storeInt(src.seed, 257);
        b_0.storeInt(src.batchIndex, 257);
        const b_1 = new Builder();
        b_1.storeInt(src.ticketPrice, 257);
        b_0.storeRef(b_1.endCell());
    };
}

export function loadPendingDraw(slice: Slice) {
    const sc_0 = slice;
    const _cycleId = sc_0.loadIntBig(257);
    const _seed = sc_0.loadIntBig(257);
    const _batchIndex = sc_0.loadIntBig(257);
    const sc_1 = sc_0.loadRef().beginParse();
    const _ticketPrice = sc_1.loadIntBig(257);
    return { $$type: 'PendingDraw' as const, cycleId: _cycleId, seed: _seed, batchIndex: _batchIndex, ticketPrice: _ticketPrice };
}

export function loadTuplePendingDraw(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _seed = source.readBigNumber();
    const _batchIndex = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    return { $$type: 'PendingDraw' as const, cycleId: _cycleId, seed: _seed, batchIndex: _batchIndex, ticketPrice: _ticketPrice };
}

export function loadGetterTuplePendingDraw(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _seed = source.readBigNumber();
    const _batchIndex = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    return { $$type: 'PendingDraw' as const, cycleId: _cycleId, seed: _seed, batchIndex: _batchIndex, ticketPrice: _ticketPrice };
}

export function storeTuplePendingDraw(source: PendingDraw) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.cycleId);
    builder.writeNumber(source.seed);
    builder.writeNumber(source.batchIndex);
    builder.writeNumber(source.ticketPrice);
    return builder.build();
}

export function dictValueParserPendingDraw(): DictionaryValue<PendingDraw> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePendingDraw(src)).endCell());
        },
        parse: (src) => {
            return loadPendingDraw(src.loadRef().beginParse());
        }
    }
}

export type UserTickets = {
    $$type: 'UserTickets';
    cycleId: bigint;
    count: bigint;
    ticketsOwned: Dictionary<bigint, boolean>;
}

export function storeUserTickets(src: UserTickets) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.cycleId, 257);
        b_0.storeInt(src.count, 257);
        b_0.storeDict(src.ticketsOwned, Dictionary.Keys.BigInt(257), Dictionary.Values.Bool());
    };
}

export function loadUserTickets(slice: Slice) {
    const sc_0 = slice;
    const _cycleId = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    const _ticketsOwned = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), sc_0);
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function loadTupleUserTickets(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    const _ticketsOwned = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), source.readCellOpt());
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function loadGetterTupleUserTickets(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    const _ticketsOwned = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), source.readCellOpt());
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function storeTupleUserTickets(source: UserTickets) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.cycleId);
    builder.writeNumber(source.count);
    builder.writeCell(source.ticketsOwned.size > 0 ? beginCell().storeDictDirect(source.ticketsOwned, Dictionary.Keys.BigInt(257), Dictionary.Values.Bool()).endCell() : null);
    return builder.build();
}

export function dictValueParserUserTickets(): DictionaryValue<UserTickets> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeUserTickets(src)).endCell());
        },
        parse: (src) => {
            return loadUserTickets(src.loadRef().beginParse());
        }
    }
}

export type PlayerInfo = {
    $$type: 'PlayerInfo';
    referrer: Address;
    userLevel: bigint;
    winningJackpotAmount: bigint;
    ticketsBought: Dictionary<bigint, UserTickets>;
    referredCount: bigint;
    consecutiveWins: Dictionary<bigint, bigint>;
    lastWinCycle: Dictionary<bigint, bigint>;
    inRowBonus: bigint;
    buyBonus: Dictionary<bigint, bigint>;
    soldBonus: bigint;
    referralBonus: bigint;
    ticketsSoldByReferrals: bigint;
    megaTicketsBought: UserTickets;
}

export function storePlayerInfo(src: PlayerInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.referrer);
        b_0.storeInt(src.userLevel, 257);
        b_0.storeInt(src.winningJackpotAmount, 257);
        b_0.storeDict(src.ticketsBought, Dictionary.Keys.BigInt(257), dictValueParserUserTickets());
        const b_1 = new Builder();
        b_1.storeInt(src.referredCount, 257);
        b_1.storeDict(src.consecutiveWins, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeDict(src.lastWinCycle, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeInt(src.inRowBonus, 257);
        b_1.storeDict(src.buyBonus, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeInt(src.soldBonus, 257);
        const b_2 = new Builder();
        b_2.storeInt(src.referralBonus, 257);
        b_2.storeInt(src.ticketsSoldByReferrals, 257);
        const b_3 = new Builder();
        b_3.store(storeUserTickets(src.megaTicketsBought));
        b_2.storeRef(b_3.endCell());
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadPlayerInfo(slice: Slice) {
    const sc_0 = slice;
    const _referrer = sc_0.loadAddress();
    const _userLevel = sc_0.loadIntBig(257);
    const _winningJackpotAmount = sc_0.loadIntBig(257);
    const _ticketsBought = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), sc_0);
    const sc_1 = sc_0.loadRef().beginParse();
    const _referredCount = sc_1.loadIntBig(257);
    const _consecutiveWins = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _lastWinCycle = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _inRowBonus = sc_1.loadIntBig(257);
    const _buyBonus = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _soldBonus = sc_1.loadIntBig(257);
    const sc_2 = sc_1.loadRef().beginParse();
    const _referralBonus = sc_2.loadIntBig(257);
    const _ticketsSoldByReferrals = sc_2.loadIntBig(257);
    const sc_3 = sc_2.loadRef().beginParse();
    const _megaTicketsBought = loadUserTickets(sc_3);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function loadTuplePlayerInfo(source: TupleReader) {
    const _referrer = source.readAddress();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _ticketsBought = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), source.readCellOpt());
    const _referredCount = source.readBigNumber();
    const _consecutiveWins = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _lastWinCycle = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _inRowBonus = source.readBigNumber();
    const _buyBonus = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    const _megaTicketsBought = loadTupleUserTickets(source);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function loadGetterTuplePlayerInfo(source: TupleReader) {
    const _referrer = source.readAddress();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _ticketsBought = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), source.readCellOpt());
    const _referredCount = source.readBigNumber();
    const _consecutiveWins = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _lastWinCycle = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _inRowBonus = source.readBigNumber();
    const _buyBonus = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    const _megaTicketsBought = loadGetterTupleUserTickets(source);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function storeTuplePlayerInfo(source: PlayerInfo) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.referrer);
    builder.writeNumber(source.userLevel);
    builder.writeNumber(source.winningJackpotAmount);
    builder.writeCell(source.ticketsBought.size > 0 ? beginCell().storeDictDirect(source.ticketsBought, Dictionary.Keys.BigInt(257), dictValueParserUserTickets()).endCell() : null);
    builder.writeNumber(source.referredCount);
    builder.writeCell(source.consecutiveWins.size > 0 ? beginCell().storeDictDirect(source.consecutiveWins, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeCell(source.lastWinCycle.size > 0 ? beginCell().storeDictDirect(source.lastWinCycle, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeNumber(source.inRowBonus);
    builder.writeCell(source.buyBonus.size > 0 ? beginCell().storeDictDirect(source.buyBonus, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeNumber(source.soldBonus);
    builder.writeNumber(source.referralBonus);
    builder.writeNumber(source.ticketsSoldByReferrals);
    builder.writeTuple(storeTupleUserTickets(source.megaTicketsBought));
    return builder.build();
}

export function dictValueParserPlayerInfo(): DictionaryValue<PlayerInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerInfo(src.loadRef().beginParse());
        }
    }
}

export type WinningTicketsList = {
    $$type: 'WinningTicketsList';
    winningTicketIds: Dictionary<bigint, bigint>;
    winningTicketAmounts: Dictionary<bigint, bigint>;
}

export function storeWinningTicketsList(src: WinningTicketsList) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeDict(src.winningTicketIds, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_0.storeDict(src.winningTicketAmounts, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
    };
}

export function loadWinningTicketsList(slice: Slice) {
    const sc_0 = slice;
    const _winningTicketIds = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_0);
    const _winningTicketAmounts = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_0);
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function loadTupleWinningTicketsList(source: TupleReader) {
    const _winningTicketIds = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _winningTicketAmounts = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function loadGetterTupleWinningTicketsList(source: TupleReader) {
    const _winningTicketIds = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _winningTicketAmounts = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function storeTupleWinningTicketsList(source: WinningTicketsList) {
    const builder = new TupleBuilder();
    builder.writeCell(source.winningTicketIds.size > 0 ? beginCell().storeDictDirect(source.winningTicketIds, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeCell(source.winningTicketAmounts.size > 0 ? beginCell().storeDictDirect(source.winningTicketAmounts, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    return builder.build();
}

export function dictValueParserWinningTicketsList(): DictionaryValue<WinningTicketsList> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeWinningTicketsList(src)).endCell());
        },
        parse: (src) => {
            return loadWinningTicketsList(src.loadRef().beginParse());
        }
    }
}

export type PlayerBasicInfo = {
    $$type: 'PlayerBasicInfo';
    exists: boolean;
    userLevel: bigint;
    winningJackpotAmount: bigint;
    referredCount: bigint;
    inRowBonus: bigint;
    soldBonus: bigint;
    referralBonus: bigint;
    ticketsSoldByReferrals: bigint;
}

export function storePlayerBasicInfo(src: PlayerBasicInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.exists);
        b_0.storeInt(src.userLevel, 257);
        b_0.storeInt(src.winningJackpotAmount, 257);
        b_0.storeInt(src.referredCount, 257);
        const b_1 = new Builder();
        b_1.storeInt(src.inRowBonus, 257);
        b_1.storeInt(src.soldBonus, 257);
        b_1.storeInt(src.referralBonus, 257);
        const b_2 = new Builder();
        b_2.storeInt(src.ticketsSoldByReferrals, 257);
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadPlayerBasicInfo(slice: Slice) {
    const sc_0 = slice;
    const _exists = sc_0.loadBit();
    const _userLevel = sc_0.loadIntBig(257);
    const _winningJackpotAmount = sc_0.loadIntBig(257);
    const _referredCount = sc_0.loadIntBig(257);
    const sc_1 = sc_0.loadRef().beginParse();
    const _inRowBonus = sc_1.loadIntBig(257);
    const _soldBonus = sc_1.loadIntBig(257);
    const _referralBonus = sc_1.loadIntBig(257);
    const sc_2 = sc_1.loadRef().beginParse();
    const _ticketsSoldByReferrals = sc_2.loadIntBig(257);
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function loadTuplePlayerBasicInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _referredCount = source.readBigNumber();
    const _inRowBonus = source.readBigNumber();
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function loadGetterTuplePlayerBasicInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _referredCount = source.readBigNumber();
    const _inRowBonus = source.readBigNumber();
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function storeTuplePlayerBasicInfo(source: PlayerBasicInfo) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.exists);
    builder.writeNumber(source.userLevel);
    builder.writeNumber(source.winningJackpotAmount);
    builder.writeNumber(source.referredCount);
    builder.writeNumber(source.inRowBonus);
    builder.writeNumber(source.soldBonus);
    builder.writeNumber(source.referralBonus);
    builder.writeNumber(source.ticketsSoldByReferrals);
    return builder.build();
}

export function dictValueParserPlayerBasicInfo(): DictionaryValue<PlayerBasicInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerBasicInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerBasicInfo(src.loadRef().beginParse());
        }
    }
}

export type PlayerTicketsInfo = {
    $$type: 'PlayerTicketsInfo';
    exists: boolean;
    cycleId: bigint;
    count: bigint;
}

export function storePlayerTicketsInfo(src: PlayerTicketsInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.exists);
        b_0.storeInt(src.cycleId, 257);
        b_0.storeInt(src.count, 257);
    };
}

export function loadPlayerTicketsInfo(slice: Slice) {
    const sc_0 = slice;
    const _exists = sc_0.loadBit();
    const _cycleId = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function loadTuplePlayerTicketsInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function loadGetterTuplePlayerTicketsInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function storeTuplePlayerTicketsInfo(source: PlayerTicketsInfo) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.exists);
    builder.writeNumber(source.cycleId);
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserPlayerTicketsInfo(): DictionaryValue<PlayerTicketsInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerTicketsInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerTicketsInfo(src.loadRef().beginParse());
        }
    }
}

export type MegaJackpotInfo = {
    $$type: 'MegaJackpotInfo';
    balance: bigint;
    isActive: boolean;
    ticketsSold: bigint;
    ticketsRequired: bigint;
    ticketPrice: bigint;
    prizeAmount: bigint;
    threshold: bigint;
}

export function storeMegaJackpotInfo(src: MegaJackpotInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.balance, 257);
        b_0.storeBit(src.isActive);
        b_0.storeInt(src.ticketsSold, 257);
        b_0.storeInt(src.ticketsRequired, 257);
        const b_1 = new Builder();
        b_1.storeInt(src.ticketPrice, 257);
        b_1.storeInt(src.prizeAmount, 257);
        b_1.storeInt(src.threshold, 257);
        b_0.storeRef(b_1.endCell());
    };
}

export function loadMegaJackpotInfo(slice: Slice) {
    const sc_0 = slice;
    const _balance = sc_0.loadIntBig(257);
    const _isActive = sc_0.loadBit();
    const _ticketsSold = sc_0.loadIntBig(257);
    const _ticketsRequired = sc_0.loadIntBig(257);
    const sc_1 = sc_0.loadRef().beginParse();
    const _ticketPrice = sc_1.loadIntBig(257);
    const _prizeAmount = sc_1.loadIntBig(257);
    const _threshold = sc_1.loadIntBig(257);
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function loadTupleMegaJackpotInfo(source: TupleReader) {
    const _balance = source.readBigNumber();
    const _isActive = source.readBoolean();
    const _ticketsSold = source.readBigNumber();
    const _ticketsRequired = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    const _threshold = source.readBigNumber();
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function loadGetterTupleMegaJackpotInfo(source: TupleReader) {
    const _balance = source.readBigNumber();
    const _isActive = source.readBoolean();
    const _ticketsSold = source.readBigNumber();
    const _ticketsRequired = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    const _threshold = source.readBigNumber();
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function storeTupleMegaJackpotInfo(source: MegaJackpotInfo) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.balance);
    builder.writeBoolean(source.isActive);
    builder.writeNumber(source.ticketsSold);
    builder.writeNumber(source.ticketsRequired);
    builder.writeNumber(source.ticketPrice);
    builder.writeNumber(source.prizeAmount);
    builder.writeNumber(source.threshold);
    return builder.build();
}

export function dictValueParserMegaJackpotInfo(): DictionaryValue<MegaJackpotInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMegaJackpotInfo(src)).endCell());
        },
        parse: (src) => {
            return loadMegaJackpotInfo(src.loadRef().beginParse());
        }
    }
}

export type Register = {
    $$type: 'Register';
    user: Address;
    referrer: Address;
}

export function storeRegister(src: Register) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2206064315, 32);
        b_0.storeAddress(src.user);
        b_0.storeAddress(src.referrer);
    };
}

export function loadRegister(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2206064315) { throw Error('Invalid prefix'); }
    const _user = sc_0.loadAddress();
    const _referrer = sc_0.loadAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function loadTupleRegister(source: TupleReader) {
    const _user = source.readAddress();
    const _referrer = source.readAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function loadGetterTupleRegister(source: TupleReader) {
    const _user = source.readAddress();
    const _referrer = source.readAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function storeTupleRegister(source: Register) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.user);
    builder.writeAddress(source.referrer);
    return builder.build();
}

export function dictValueParserRegister(): DictionaryValue<Register> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeRegister(src)).endCell());
        },
        parse: (src) => {
            return loadRegister(src.loadRef().beginParse());
        }
    }
}

export type BuyTicket = {
    $$type: 'BuyTicket';
    level: bigint;
    count: bigint;
}

export function storeBuyTicket(src: BuyTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(264192202, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.count, 257);
    };
}

export function loadBuyTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 264192202) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function loadTupleBuyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function loadGetterTupleBuyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function storeTupleBuyTicket(source: BuyTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserBuyTicket(): DictionaryValue<BuyTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyLuckyTicket = {
    $$type: 'BuyLuckyTicket';
    level: bigint;
    ticketId: bigint;
}

export function storeBuyLuckyTicket(src: BuyLuckyTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1049254189, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.ticketId, 257);
    };
}

export function loadBuyLuckyTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1049254189) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _ticketId = sc_0.loadIntBig(257);
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function loadTupleBuyLuckyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function loadGetterTupleBuyLuckyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function storeTupleBuyLuckyTicket(source: BuyLuckyTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.ticketId);
    return builder.build();
}

export function dictValueParserBuyLuckyTicket(): DictionaryValue<BuyLuckyTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyLuckyTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyLuckyTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyMegaTicket = {
    $$type: 'BuyMegaTicket';
    count: bigint;
}

export function storeBuyMegaTicket(src: BuyMegaTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(764815221, 32);
        b_0.storeInt(src.count, 257);
    };
}

export function loadBuyMegaTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 764815221) { throw Error('Invalid prefix'); }
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function loadTupleBuyMegaTicket(source: TupleReader) {
    const _count = source.readBigNumber();
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function loadGetterTupleBuyMegaTicket(source: TupleReader) {
    const _count = source.readBigNumber();
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function storeTupleBuyMegaTicket(source: BuyMegaTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserBuyMegaTicket(): DictionaryValue<BuyMegaTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyMegaTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyMegaTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyLuckyMegaTicket = {
    $$type: 'BuyLuckyMegaTicket';
    ticketId: bigint;
}

export function storeBuyLuckyMegaTicket(src: BuyLuckyMegaTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(4112755696, 32);
        b_0.storeInt(src.ticketId, 257);
    };
}

export function loadBuyLuckyMegaTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 4112755696) { throw Error('Invalid prefix'); }
    const _ticketId = sc_0.loadIntBig(257);
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function loadTupleBuyLuckyMegaTicket(source: TupleReader) {
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function loadGetterTupleBuyLuckyMegaTicket(source: TupleReader) {
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function storeTupleBuyLuckyMegaTicket(source: BuyLuckyMegaTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.ticketId);
    return builder.build();
}

export function dictValueParserBuyLuckyMegaTicket(): DictionaryValue<BuyLuckyMegaTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyLuckyMegaTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyLuckyMegaTicket(src.loadRef().beginParse());
        }
    }
}

export type MultiLevelLottery$Data = {
    $$type: 'MultiLevelLottery$Data';
    owner: Address;
    admin: Address;
    refRoot: Address;
    basePrice: bigint;
    levelCfg: Dictionary<bigint, LevelConfig>;
    cycles: Dictionary<bigint, Cycle>;
    pendingDraws: Dictionary<bigint, PendingDraw>;
    winningTickets: Dictionary<bigint, WinningTicketsList>;
    players: Dictionary<Address, PlayerInfo>;
    megaJackpot: bigint;
    megaActive: boolean;
    megaCycleId: bigint;
    megaTicketsSold: bigint;
    megaTicketsRequired: bigint;
    megaTickets: Dictionary<bigint, Address>;
    megaTicketPrice: bigint;
    megaPrizeAmount: bigint;
    megaThresholdMultiplier: bigint;
    megaTicketPriceMultiplier: bigint;
    megaDefaultTicketCount: bigint;
    megaNextTicket: bigint;
}

export function storeMultiLevelLottery$Data(src: MultiLevelLottery$Data) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.owner);
        b_0.storeAddress(src.admin);
        b_0.storeAddress(src.refRoot);
        const b_1 = new Builder();
        b_1.storeInt(src.basePrice, 257);
        b_1.storeDict(src.levelCfg, Dictionary.Keys.BigInt(257), dictValueParserLevelConfig());
        b_1.storeDict(src.cycles, Dictionary.Keys.BigInt(257), dictValueParserCycle());
        b_1.storeDict(src.pendingDraws, Dictionary.Keys.BigInt(257), dictValueParserPendingDraw());
        const b_2 = new Builder();
        b_2.storeDict(src.winningTickets, Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList());
        b_2.storeDict(src.players, Dictionary.Keys.Address(), dictValueParserPlayerInfo());
        b_2.storeInt(src.megaJackpot, 257);
        b_2.storeBit(src.megaActive);
        b_2.storeInt(src.megaCycleId, 257);
        b_2.storeInt(src.megaTicketsSold, 257);
        const b_3 = new Builder();
        b_3.storeInt(src.megaTicketsRequired, 257);
        b_3.storeDict(src.megaTickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address());
        b_3.storeInt(src.megaTicketPrice, 257);
        b_3.storeInt(src.megaPrizeAmount, 257);
        const b_4 = new Builder();
        b_4.storeInt(src.megaThresholdMultiplier, 257);
        b_4.storeInt(src.megaTicketPriceMultiplier, 257);
        b_4.storeInt(src.megaDefaultTicketCount, 257);
        const b_5 = new Builder();
        b_5.storeInt(src.megaNextTicket, 257);
        b_4.storeRef(b_5.endCell());
        b_3.storeRef(b_4.endCell());
        b_2.storeRef(b_3.endCell());
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadMultiLevelLottery$Data(slice: Slice) {
    const sc_0 = slice;
    const _owner = sc_0.loadAddress();
    const _admin = sc_0.loadAddress();
    const _refRoot = sc_0.loadAddress();
    const sc_1 = sc_0.loadRef().beginParse();
    const _basePrice = sc_1.loadIntBig(257);
    const _levelCfg = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), sc_1);
    const _cycles = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserCycle(), sc_1);
    const _pendingDraws = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserPendingDraw(), sc_1);
    const sc_2 = sc_1.loadRef().beginParse();
    const _winningTickets = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), sc_2);
    const _players = Dictionary.load(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), sc_2);
    const _megaJackpot = sc_2.loadIntBig(257);
    const _megaActive = sc_2.loadBit();
    const _megaCycleId = sc_2.loadIntBig(257);
    const _megaTicketsSold = sc_2.loadIntBig(257);
    const sc_3 = sc_2.loadRef().beginParse();
    const _megaTicketsRequired = sc_3.loadIntBig(257);
    const _megaTickets = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), sc_3);
    const _megaTicketPrice = sc_3.loadIntBig(257);
    const _megaPrizeAmount = sc_3.loadIntBig(257);
    const sc_4 = sc_3.loadRef().beginParse();
    const _megaThresholdMultiplier = sc_4.loadIntBig(257);
    const _megaTicketPriceMultiplier = sc_4.loadIntBig(257);
    const _megaDefaultTicketCount = sc_4.loadIntBig(257);
    const sc_5 = sc_4.loadRef().beginParse();
    const _megaNextTicket = sc_5.loadIntBig(257);
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, admin: _admin, refRoot: _refRoot, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, pendingDraws: _pendingDraws, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount, megaThresholdMultiplier: _megaThresholdMultiplier, megaTicketPriceMultiplier: _megaTicketPriceMultiplier, megaDefaultTicketCount: _megaDefaultTicketCount, megaNextTicket: _megaNextTicket };
}

export function loadTupleMultiLevelLottery$Data(source: TupleReader) {
    const _owner = source.readAddress();
    const _admin = source.readAddress();
    const _refRoot = source.readAddress();
    const _basePrice = source.readBigNumber();
    const _levelCfg = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), source.readCellOpt());
    const _cycles = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserCycle(), source.readCellOpt());
    const _pendingDraws = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserPendingDraw(), source.readCellOpt());
    const _winningTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), source.readCellOpt());
    const _players = Dictionary.loadDirect(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), source.readCellOpt());
    const _megaJackpot = source.readBigNumber();
    const _megaActive = source.readBoolean();
    const _megaCycleId = source.readBigNumber();
    const _megaTicketsSold = source.readBigNumber();
    const _megaTicketsRequired = source.readBigNumber();
    source = source.readTuple();
    const _megaTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    const _megaTicketPrice = source.readBigNumber();
    const _megaPrizeAmount = source.readBigNumber();
    const _megaThresholdMultiplier = source.readBigNumber();
    const _megaTicketPriceMultiplier = source.readBigNumber();
    const _megaDefaultTicketCount = source.readBigNumber();
    const _megaNextTicket = source.readBigNumber();
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, admin: _admin, refRoot: _refRoot, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, pendingDraws: _pendingDraws, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount, megaThresholdMultiplier: _megaThresholdMultiplier, megaTicketPriceMultiplier: _megaTicketPriceMultiplier, megaDefaultTicketCount: _megaDefaultTicketCount, megaNextTicket: _megaNextTicket };
}

export function loadGetterTupleMultiLevelLottery$Data(source: TupleReader) {
    const _owner = source.readAddress();
    const _admin = source.readAddress();
    const _refRoot = source.readAddress();
    const _basePrice = source.readBigNumber();
    const _levelCfg = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), source.readCellOpt());
    const _cycles = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserCycle(), source.readCellOpt());
    const _pendingDraws = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserPendingDraw(), source.readCellOpt());
    const _winningTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), source.readCellOpt());
    const _players = Dictionary.loadDirect(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), source.readCellOpt());
    const _megaJackpot = source.readBigNumber();
    const _megaActive = source.readBoolean();
    const _megaCycleId = source.readBigNumber();
    const _megaTicketsSold = source.readBigNumber();
    const _megaTicketsRequired = source.readBigNumber();
    const _megaTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    const _megaTicketPrice = source.readBigNumber();
    const _megaPrizeAmount = source.readBigNumber();
    const _megaThresholdMultiplier = source.readBigNumber();
    const _megaTicketPriceMultiplier = source.readBigNumber();
    const _megaDefaultTicketCount = source.readBigNumber();
    const _megaNextTicket = source.readBigNumber();
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, admin: _admin, refRoot: _refRoot, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, pendingDraws: _pendingDraws, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount, megaThresholdMultiplier: _megaThresholdMultiplier, megaTicketPriceMultiplier: _megaTicketPriceMultiplier, megaDefaultTicketCount: _megaDefaultTicketCount, megaNextTicket: _megaNextTicket };
}

export function storeTupleMultiLevelLottery$Data(source: MultiLevelLottery$Data) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.owner);
    builder.writeAddress(source.admin);
    builder.writeAddress(source.refRoot);
    builder.writeNumber(source.basePrice);
    builder.writeCell(source.levelCfg.size > 0 ? beginCell().storeDictDirect(source.levelCfg, Dictionary.Keys.BigInt(257), dictValueParserLevelConfig()).endCell() : null);
    builder.writeCell(source.cycles.size > 0 ? beginCell().storeDictDirect(source.cycles, Dictionary.Keys.BigInt(257), dictValueParserCycle()).endCell() : null);
    builder.writeCell(source.pendingDraws.size > 0 ? beginCell().storeDictDirect(source.pendingDraws, Dictionary.Keys.BigInt(257), dictValueParserPendingDraw()).endCell() : null);
    builder.writeCell(source.winningTickets.size > 0 ? beginCell().storeDictDirect(source.winningTickets, Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList()).endCell() : null);
    builder.writeCell(source.players.size > 0 ? beginCell().storeDictDirect(source.players, Dictionary.Keys.Address(), dictValueParserPlayerInfo()).endCell() : null);
    builder.writeNumber(source.megaJackpot);
    builder.writeBoolean(source.megaActive);
    builder.writeNumber(source.megaCycleId);
    builder.writeNumber(source.megaTicketsSold);
    builder.writeNumber(source.megaTicketsRequired);
    builder.writeCell(source.megaTickets.size > 0 ? beginCell().storeDictDirect(source.megaTickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address()).endCell() : null);
    builder.writeNumber(source.megaTicketPrice);
    builder.writeNumber(source.megaPrizeAmount);
    builder.writeNumber(source.megaThresholdMultiplier);
    builder.writeNumber(source.megaTicketPriceMultiplier);
    builder.writeNumber(source.megaDefaultTicketCount);
    builder.writeNumber(source.megaNextTicket);
    return builder.build();
}

export function dictValueParserMultiLevelLottery$Data(): DictionaryValue<MultiLevelLottery$Data> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMultiLevelLottery$Data(src)).endCell());
        },
        parse: (src) => {
            return loadMultiLevelLottery$Data(src.loadRef().beginParse());
        }
    }
}

 type MultiLevelLottery_init_args = {
    $$type: 'MultiLevelLottery_init_args';
    owner: Address;
}

function initMultiLevelLottery_init_args(src: MultiLevelLottery_init_args) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.owner);
    };
}

async function MultiLevelLottery_init(owner: Address) {
    const __code = Cell.fromHex('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');
    const builder = beginCell();
    builder.storeUint(0, 1);
    initMultiLevelLottery_init_args({ $$type: 'MultiLevelLottery_init_args', owner })(builder);
    const __data = builder.endCell();
    return { code: __code, data: __data };
}

export const MultiLevelLottery_errors = {
    2: { message: "Stack underflow" },
    3: { message: "Stack overflow" },
    4: { message: "Integer overflow" },
    5: { message: "Integer out of expected range" },
    6: { message: "Invalid opcode" },
    7: { message: "Type check error" },
    8: { message: "Cell overflow" },
    9: { message: "Cell underflow" },
    10: { message: "Dictionary error" },
    11: { message: "'Unknown' error" },
    12: { message: "Fatal error" },
    13: { message: "Out of gas error" },
    14: { message: "Virtualization error" },
    32: { message: "Action list is invalid" },
    33: { message: "Action list is too long" },
    34: { message: "Action is invalid or not supported" },
    35: { message: "Invalid source address in outbound message" },
    36: { message: "Invalid destination address in outbound message" },
    37: { message: "Not enough Toncoin" },
    38: { message: "Not enough extra currencies" },
    39: { message: "Outbound message does not fit into a cell after rewriting" },
    40: { message: "Cannot process a message" },
    41: { message: "Library reference is null" },
    42: { message: "Library change action error" },
    43: { message: "Exceeded maximum number of cells in the library or the maximum depth of the Merkle tree" },
    50: { message: "Account state size exceeded limits" },
    128: { message: "Null reference exception" },
    129: { message: "Invalid serialization prefix" },
    130: { message: "Invalid incoming message" },
    131: { message: "Constraints error" },
    132: { message: "Access denied" },
    133: { message: "Contract stopped" },
    134: { message: "Invalid argument" },
    135: { message: "Code of a contract was not found" },
    136: { message: "Invalid standard address" },
    138: { message: "Not a basechain address" },
    2404: { message: "BAD_OWNER" },
    3926: { message: "NOT_ENOUGH_TON" },
    10632: { message: "CANNOT_CHANGE_WHILE_ACTIVE" },
    12543: { message: "BAD_THRESHOLD_MULT" },
    13001: { message: "MEGA_NOT_ACTIVE" },
    13082: { message: "NO_MEGA_WINNER" },
    15380: { message: "MEGA_ALREADY_ACTIVE" },
    15475: { message: "TOO_MANY_MEGA_TICKETS" },
    15618: { message: "INVALID_MEGA_TICKET_ID" },
    20915: { message: "CYCLE_FINISHED" },
    23656: { message: "NOT_ENOUGH_MEGA_TICKETS_AVAILABLE" },
    24082: { message: "NOT_ENOUGH_AVAILABLE_TICKETS" },
    24243: { message: "BAD_MULT" },
    24524: { message: "BAD_TICKET_PRICE_MULT" },
    29392: { message: "BAD_AMOUNT" },
    31794: { message: "BAD_PRIZE_AMOUNT" },
    33070: { message: "BAD_COUNT" },
    35040: { message: "DRAW_PENDING" },
    35867: { message: "BAD_PRICE" },
    36633: { message: "BAD_ADMIN" },
    37469: { message: "ONLY_OWNER" },
    49061: { message: "TICKET_ALREADY_TAKEN" },
    52932: { message: "NO_PENDING_DRAW" },
    53267: { message: "ALREADY_REGISTERED" },
    54253: { message: "ONLY_OWNER_OR_ADMIN" },
    56464: { message: "BAD_TICKET_COUNT" },
    57100: { message: "MEGA_TICKET_ALREADY_TAKEN" },
    58403: { message: "INVALID_TICKET_ID" },
    61473: { message: "NO_CYCLE" },
    62110: { message: "BAD_LEVEL" },
    62587: { message: "TOO_MANY_TICKETS" },
} as const

export const MultiLevelLottery_errors_backward = {
    "Stack underflow": 2,
    "Stack overflow": 3,
    "Integer overflow": 4,
    "Integer out of expected range": 5,
    "Invalid opcode": 6,
    "Type check error": 7,
    "Cell overflow": 8,
    "Cell underflow": 9,
    "Dictionary error": 10,
    "'Unknown' error": 11,
    "Fatal error": 12,
    "Out of gas error": 13,
    "Virtualization error": 14,
    "Action list is invalid": 32,
    "Action list is too long": 33,
    "Action is invalid or not supported": 34,
    "Invalid source address in outbound message": 35,
    "Invalid destination address in outbound message": 36,
    "Not enough Toncoin": 37,
    "Not enough extra currencies": 38,
    "Outbound message does not fit into a cell after rewriting": 39,
    "Cannot process a message": 40,
    "Library reference is null": 41,
    "Library change action error": 42,
    "Exceeded maximum number of cells in the library or the maximum depth of the Merkle tree": 43,
    "Account state size exceeded limits": 50,
    "Null reference exception": 128,
    "Invalid serialization prefix": 129,
    "Invalid incoming message": 130,
    "Constraints error": 131,
    "Access denied": 132,
    "Contract stopped": 133,
    "Invalid argument": 134,
    "Code of a contract was not found": 135,
    "Invalid standard address": 136,
    "Not a basechain address": 138,
    "BAD_OWNER": 2404,
    "NOT_ENOUGH_TON": 3926,
    "CANNOT_CHANGE_WHILE_ACTIVE": 10632,
    "BAD_THRESHOLD_MULT": 12543,
    "MEGA_NOT_ACTIVE": 13001,
    "NO_MEGA_WINNER": 13082,
    "MEGA_ALREADY_ACTIVE": 15380,
    "TOO_MANY_MEGA_TICKETS": 15475,
    "INVALID_MEGA_TICKET_ID": 15618,
    "CYCLE_FINISHED": 20915,
    "NOT_ENOUGH_MEGA_TICKETS_AVAILABLE": 23656,
    "NOT_ENOUGH_AVAILABLE_TICKETS": 24082,
    "BAD_MULT": 24243,
    "BAD_TICKET_PRICE_MULT": 24524,
    "BAD_AMOUNT": 29392,
    "BAD_PRIZE_AMOUNT": 31794,
    "BAD_COUNT": 33070,
    "DRAW_PENDING": 35040,
    "BAD_PRICE": 35867,
    "BAD_ADMIN": 36633,
    "ONLY_OWNER": 37469,
    "TICKET_ALREADY_TAKEN": 49061,
    "NO_PENDING_DRAW": 52932,
    "ALREADY_REGISTERED": 53267,
    "ONLY_OWNER_OR_ADMIN": 54253,
    "BAD_TICKET_COUNT": 56464,
    "MEGA_TICKET_ALREADY_TAKEN": 57100,
    "INVALID_TICKET_ID": 58403,
    "NO_CYCLE": 61473,
    "BAD_LEVEL": 62110,
    "TOO_MANY_TICKETS": 62587,
} as const

const MultiLevelLottery_types: ABIType[] = [
    {"name":"DataSize","header":null,"fields":[{"name":"cells","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"bits","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"refs","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"SignedBundle","header":null,"fields":[{"name":"signature","type":{"kind":"simple","type":"fixed-bytes","optional":false,"format":64}},{"name":"signedData","type":{"kind":"simple","type":"slice","optional":false,"format":"remainder"}}]},
    {"name":"StateInit","header":null,"fields":[{"name":"code","type":{"kind":"simple","type":"cell","optional":false}},{"name":"data","type":{"kind":"simple","type":"cell","optional":false}}]},
    {"name":"Context","header":null,"fields":[{"name":"bounceable","type":{"kind":"simple","type":"bool","optional":false}},{"name":"sender","type":{"kind":"simple","type":"address","optional":false}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"raw","type":{"kind":"simple","type":"slice","optional":false}}]},
    {"name":"SendParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"code","type":{"kind":"simple","type":"cell","optional":true}},{"name":"data","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"to","type":{"kind":"simple","type":"address","optional":false}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}}]},
    {"name":"MessageParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"to","type":{"kind":"simple","type":"address","optional":false}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}}]},
    {"name":"DeployParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}},{"name":"init","type":{"kind":"simple","type":"StateInit","optional":false}}]},
    {"name":"StdAddress","header":null,"fields":[{"name":"workchain","type":{"kind":"simple","type":"int","optional":false,"format":8}},{"name":"address","type":{"kind":"simple","type":"uint","optional":false,"format":256}}]},
    {"name":"VarAddress","header":null,"fields":[{"name":"workchain","type":{"kind":"simple","type":"int","optional":false,"format":32}},{"name":"address","type":{"kind":"simple","type":"slice","optional":false}}]},
    {"name":"BasechainAddress","header":null,"fields":[{"name":"hash","type":{"kind":"simple","type":"int","optional":true,"format":257}}]},
    {"name":"Deploy","header":2490013878,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}}]},
    {"name":"DeployOk","header":2952335191,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}}]},
    {"name":"FactoryDeploy","header":1829761339,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}},{"name":"cashback","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerSetBasePrice","header":1268395613,"fields":[{"name":"newPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"OwnerSetLevelMultiplier","header":2399467553,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"k","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"OwnerWithdraw","header":2082387357,"fields":[{"name":"amount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"recipient","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerTransferOwnership","header":2073089375,"fields":[{"name":"newOwner","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerSetAdmin","header":1015550025,"fields":[{"name":"newAdmin","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerActivateMega","header":2107791225,"fields":[{"name":"ticketCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"prizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"OwnerSetMegaConfig","header":1091523188,"fields":[{"name":"thresholdMultiplier","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketPriceMultiplier","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"defaultTicketCount","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"ExecuteDrawBatch","header":2969230544,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"LevelConfig","header":null,"fields":[{"name":"k","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"Cycle","header":null,"fields":[{"name":"id","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"isFinished","type":{"kind":"simple","type":"bool","optional":false}},{"name":"nextTicket","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"tickets","type":{"kind":"dict","key":"int","value":"address"}}]},
    {"name":"PendingDraw","header":null,"fields":[{"name":"cycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"seed","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"batchIndex","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"UserTickets","header":null,"fields":[{"name":"cycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsOwned","type":{"kind":"dict","key":"int","value":"bool"}}]},
    {"name":"PlayerInfo","header":null,"fields":[{"name":"referrer","type":{"kind":"simple","type":"address","optional":false}},{"name":"userLevel","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"winningJackpotAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsBought","type":{"kind":"dict","key":"int","value":"UserTickets","valueFormat":"ref"}},{"name":"referredCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"consecutiveWins","type":{"kind":"dict","key":"int","value":"int"}},{"name":"lastWinCycle","type":{"kind":"dict","key":"int","value":"int"}},{"name":"inRowBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"buyBonus","type":{"kind":"dict","key":"int","value":"int"}},{"name":"soldBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referralBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSoldByReferrals","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsBought","type":{"kind":"simple","type":"UserTickets","optional":false}}]},
    {"name":"WinningTicketsList","header":null,"fields":[{"name":"winningTicketIds","type":{"kind":"dict","key":"int","value":"int"}},{"name":"winningTicketAmounts","type":{"kind":"dict","key":"int","value":"int"}}]},
    {"name":"PlayerBasicInfo","header":null,"fields":[{"name":"exists","type":{"kind":"simple","type":"bool","optional":false}},{"name":"userLevel","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"winningJackpotAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referredCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"inRowBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"soldBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referralBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSoldByReferrals","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"PlayerTicketsInfo","header":null,"fields":[{"name":"exists","type":{"kind":"simple","type":"bool","optional":false}},{"name":"cycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"MegaJackpotInfo","header":null,"fields":[{"name":"balance","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"isActive","type":{"kind":"simple","type":"bool","optional":false}},{"name":"ticketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsRequired","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"prizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"threshold","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"Register","header":2206064315,"fields":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"referrer","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"BuyTicket","header":264192202,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyLuckyTicket","header":1049254189,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyMegaTicket","header":764815221,"fields":[{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyLuckyMegaTicket","header":4112755696,"fields":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"MultiLevelLottery$Data","header":null,"fields":[{"name":"owner","type":{"kind":"simple","type":"address","optional":false}},{"name":"admin","type":{"kind":"simple","type":"address","optional":false}},{"name":"refRoot","type":{"kind":"simple","type":"address","optional":false}},{"name":"basePrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"levelCfg","type":{"kind":"dict","key":"int","value":"LevelConfig","valueFormat":"ref"}},{"name":"cycles","type":{"kind":"dict","key":"int","value":"Cycle","valueFormat":"ref"}},{"name":"pendingDraws","type":{"kind":"dict","key":"int","value":"PendingDraw","valueFormat":"ref"}},{"name":"winningTickets","type":{"kind":"dict","key":"int","value":"WinningTicketsList","valueFormat":"ref"}},{"name":"players","type":{"kind":"dict","key":"address","value":"PlayerInfo","valueFormat":"ref"}},{"name":"megaJackpot","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaActive","type":{"kind":"simple","type":"bool","optional":false}},{"name":"megaCycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsRequired","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTickets","type":{"kind":"dict","key":"int","value":"address"}},{"name":"megaTicketPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaPrizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaThresholdMultiplier","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketPriceMultiplier","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaDefaultTicketCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaNextTicket","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
]

const MultiLevelLottery_opcodes = {
    "Deploy": 2490013878,
    "DeployOk": 2952335191,
    "FactoryDeploy": 1829761339,
    "OwnerSetBasePrice": 1268395613,
    "OwnerSetLevelMultiplier": 2399467553,
    "OwnerWithdraw": 2082387357,
    "OwnerTransferOwnership": 2073089375,
    "OwnerSetAdmin": 1015550025,
    "OwnerActivateMega": 2107791225,
    "OwnerSetMegaConfig": 1091523188,
    "ExecuteDrawBatch": 2969230544,
    "Register": 2206064315,
    "BuyTicket": 264192202,
    "BuyLuckyTicket": 1049254189,
    "BuyMegaTicket": 764815221,
    "BuyLuckyMegaTicket": 4112755696,
}

const MultiLevelLottery_getters: ABIGetter[] = [
    {"name":"getPlayerBasicInfo","methodId":82618,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"PlayerBasicInfo","optional":false}},
    {"name":"getPlayerReferrer","methodId":90665,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"address","optional":true}},
    {"name":"getPlayerTicketsInLevel","methodId":115408,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"PlayerTicketsInfo","optional":false}},
    {"name":"getPlayerMegaTickets","methodId":113255,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"PlayerTicketsInfo","optional":false}},
    {"name":"getUserMegaTicketIds","methodId":109617,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"dict","key":"int","value":"int"}},
    {"name":"hasPlayerMegaTicket","methodId":120050,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getPlayerConsecutiveWins","methodId":73091,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getPlayerLastWinCycle","methodId":73653,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getPlayerBuyBonus","methodId":122736,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"hasPlayerTicket","methodId":117898,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getUserTicketIds","methodId":85391,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"dict","key":"int","value":"int"}},
    {"name":"getUserTicketCount","methodId":124880,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getCycleBasic","methodId":126705,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"Cycle","optional":true}},
    {"name":"getCycleTicketsSold","methodId":100050,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"hasPendingDraw","methodId":90860,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getPendingDrawBatchIndex","methodId":125004,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getLevelConfig","methodId":78942,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"LevelConfig","optional":true}},
    {"name":"getLevelPrice","methodId":99207,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getWinningTickets","methodId":120154,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"WinningTicketsList","optional":true}},
    {"name":"getMegaJackpot","methodId":91237,"arguments":[],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getOwner","methodId":102025,"arguments":[],"returnType":{"kind":"simple","type":"address","optional":false}},
    {"name":"getAdmin","methodId":69338,"arguments":[],"returnType":{"kind":"simple","type":"address","optional":false}},
    {"name":"getRefRoot","methodId":77663,"arguments":[],"returnType":{"kind":"simple","type":"address","optional":false}},
    {"name":"isTicketAvailable","methodId":108921,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getMegaJackpotInfo","methodId":115452,"arguments":[],"returnType":{"kind":"simple","type":"MegaJackpotInfo","optional":false}},
    {"name":"isMegaTicketAvailable","methodId":111834,"arguments":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getMegaTicketOwner","methodId":123239,"arguments":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"address","optional":true}},
]

export const MultiLevelLottery_getterMapping: { [key: string]: string } = {
    'getPlayerBasicInfo': 'getGetPlayerBasicInfo',
    'getPlayerReferrer': 'getGetPlayerReferrer',
    'getPlayerTicketsInLevel': 'getGetPlayerTicketsInLevel',
    'getPlayerMegaTickets': 'getGetPlayerMegaTickets',
    'getUserMegaTicketIds': 'getGetUserMegaTicketIds',
    'hasPlayerMegaTicket': 'getHasPlayerMegaTicket',
    'getPlayerConsecutiveWins': 'getGetPlayerConsecutiveWins',
    'getPlayerLastWinCycle': 'getGetPlayerLastWinCycle',
    'getPlayerBuyBonus': 'getGetPlayerBuyBonus',
    'hasPlayerTicket': 'getHasPlayerTicket',
    'getUserTicketIds': 'getGetUserTicketIds',
    'getUserTicketCount': 'getGetUserTicketCount',
    'getCycleBasic': 'getGetCycleBasic',
    'getCycleTicketsSold': 'getGetCycleTicketsSold',
    'hasPendingDraw': 'getHasPendingDraw',
    'getPendingDrawBatchIndex': 'getGetPendingDrawBatchIndex',
    'getLevelConfig': 'getGetLevelConfig',
    'getLevelPrice': 'getGetLevelPrice',
    'getWinningTickets': 'getGetWinningTickets',
    'getMegaJackpot': 'getGetMegaJackpot',
    'getOwner': 'getGetOwner',
    'getAdmin': 'getGetAdmin',
    'getRefRoot': 'getGetRefRoot',
    'isTicketAvailable': 'getIsTicketAvailable',
    'getMegaJackpotInfo': 'getGetMegaJackpotInfo',
    'isMegaTicketAvailable': 'getIsMegaTicketAvailable',
    'getMegaTicketOwner': 'getGetMegaTicketOwner',
}

const MultiLevelLottery_receivers: ABIReceiver[] = [
    {"receiver":"internal","message":{"kind":"typed","type":"Register"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyLuckyTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"ExecuteDrawBatch"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyMegaTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyLuckyMegaTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetBasePrice"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetLevelMultiplier"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerWithdraw"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerTransferOwnership"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetAdmin"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerActivateMega"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetMegaConfig"}},
    {"receiver":"internal","message":{"kind":"typed","type":"Deploy"}},
]

export const LEVEL_MINING = 0n;
export const LEVEL_GOLD = 1n;
export const LEVEL_TREASURE = 2n;
export const MAX_TICKETS = 777n;
export const JACKPOT_MULT = 100n;
export const MULT_4X_COUNT = 16n;
export const MULT_2X_COUNT = 62n;
export const MULT_1X_COUNT = 125n;
export const TOTAL_WINNERS = 204n;
export const BATCH_SIZE = 20n;
export const ADMIN_WALLET = address("EQDza-zOVFYP76FflSy6bTlv7MqpVd9dnJM7x_eA23ikg_85");
export const REF_ROOT_WALLET = address("EQBXqxBsKSUY18LmujWS8IW7Cn_DrJqVtF6jm1C4GgGamvrT");

export class MultiLevelLottery implements Contract {
    
    public static readonly storageReserve = 0n;
    public static readonly errors = MultiLevelLottery_errors_backward;
    public static readonly opcodes = MultiLevelLottery_opcodes;
    
    static async init(owner: Address) {
        return await MultiLevelLottery_init(owner);
    }
    
    static async fromInit(owner: Address) {
        const __gen_init = await MultiLevelLottery_init(owner);
        const address = contractAddress(0, __gen_init);
        return new MultiLevelLottery(address, __gen_init);
    }
    
    static fromAddress(address: Address) {
        return new MultiLevelLottery(address);
    }
    
    readonly address: Address; 
    readonly init?: { code: Cell, data: Cell };
    readonly abi: ContractABI = {
        types:  MultiLevelLottery_types,
        getters: MultiLevelLottery_getters,
        receivers: MultiLevelLottery_receivers,
        errors: MultiLevelLottery_errors,
    };
    
    constructor(address: Address, init?: { code: Cell, data: Cell }) {
        this.address = address;
        this.init = init;
    }
    
    async send(provider: ContractProvider, via: Sender, args: { value: bigint, bounce?: boolean| null | undefined }, message: Register | BuyTicket | BuyLuckyTicket | ExecuteDrawBatch | BuyMegaTicket | BuyLuckyMegaTicket | OwnerSetBasePrice | OwnerSetLevelMultiplier | OwnerWithdraw | OwnerTransferOwnership | OwnerSetAdmin | OwnerActivateMega | OwnerSetMegaConfig | Deploy) {
        
        let body: Cell | null = null;
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'Register') {
            body = beginCell().store(storeRegister(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyTicket') {
            body = beginCell().store(storeBuyTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyLuckyTicket') {
            body = beginCell().store(storeBuyLuckyTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'ExecuteDrawBatch') {
            body = beginCell().store(storeExecuteDrawBatch(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyMegaTicket') {
            body = beginCell().store(storeBuyMegaTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyLuckyMegaTicket') {
            body = beginCell().store(storeBuyLuckyMegaTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetBasePrice') {
            body = beginCell().store(storeOwnerSetBasePrice(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetLevelMultiplier') {
            body = beginCell().store(storeOwnerSetLevelMultiplier(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerWithdraw') {
            body = beginCell().store(storeOwnerWithdraw(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerTransferOwnership') {
            body = beginCell().store(storeOwnerTransferOwnership(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetAdmin') {
            body = beginCell().store(storeOwnerSetAdmin(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerActivateMega') {
            body = beginCell().store(storeOwnerActivateMega(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetMegaConfig') {
            body = beginCell().store(storeOwnerSetMegaConfig(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'Deploy') {
            body = beginCell().store(storeDeploy(message)).endCell();
        }
        if (body === null) { throw new Error('Invalid message type'); }
        
        await provider.internal(via, { ...args, body: body });
        
    }
    
    async getGetPlayerBasicInfo(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerBasicInfo', builder.build())).stack;
        const result = loadGetterTuplePlayerBasicInfo(source);
        return result;
    }
    
    async getGetPlayerReferrer(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerReferrer', builder.build())).stack;
        const result = source.readAddressOpt();
        return result;
    }
    
    async getGetPlayerTicketsInLevel(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerTicketsInLevel', builder.build())).stack;
        const result = loadGetterTuplePlayerTicketsInfo(source);
        return result;
    }
    
    async getGetPlayerMegaTickets(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerMegaTickets', builder.build())).stack;
        const result = loadGetterTuplePlayerTicketsInfo(source);
        return result;
    }
    
    async getGetUserMegaTicketIds(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getUserMegaTicketIds', builder.build())).stack;
        const result = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
        return result;
    }
    
    async getHasPlayerMegaTicket(provider: ContractProvider, user: Address, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(ticketId);
        const source = (await provider.get('hasPlayerMegaTicket', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetPlayerConsecutiveWins(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerConsecutiveWins', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetPlayerLastWinCycle(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerLastWinCycle', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetPlayerBuyBonus(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerBuyBonus', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getHasPlayerTicket(provider: ContractProvider, user: Address, level: bigint, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        builder.writeNumber(ticketId);
        const source = (await provider.get('hasPlayerTicket', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetUserTicketIds(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getUserTicketIds', builder.build())).stack;
        const result = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
        return result;
    }
    
    async getGetUserTicketCount(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getUserTicketCount', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetCycleBasic(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getCycleBasic', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleCycle(result_p) : null;
        return result;
    }
    
    async getGetCycleTicketsSold(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getCycleTicketsSold', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getHasPendingDraw(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('hasPendingDraw', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetPendingDrawBatchIndex(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getPendingDrawBatchIndex', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetLevelConfig(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getLevelConfig', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleLevelConfig(result_p) : null;
        return result;
    }
    
    async getGetLevelPrice(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getLevelPrice', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetWinningTickets(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getWinningTickets', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleWinningTicketsList(result_p) : null;
        return result;
    }
    
    async getGetMegaJackpot(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getMegaJackpot', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetOwner(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getOwner', builder.build())).stack;
        const result = source.readAddress();
        return result;
    }
    
    async getGetAdmin(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getAdmin', builder.build())).stack;
        const result = source.readAddress();
        return result;
    }
    
    async getGetRefRoot(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getRefRoot', builder.build())).stack;
        const result = source.readAddress();
        return result;
    }
    
    async getIsTicketAvailable(provider: ContractProvider, level: bigint, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        builder.writeNumber(ticketId);
        const source = (await provider.get('isTicketAvailable', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetMegaJackpotInfo(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getMegaJackpotInfo', builder.build())).stack;
        const result = loadGetterTupleMegaJackpotInfo(source);
        return result;
    }
    
    async getIsMegaTicketAvailable(provider: ContractProvider, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(ticketId);
        const source = (await provider.get('isMegaTicketAvailable', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetMegaTicketOwner(provider: ContractProvider, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(ticketId);
        const source = (await provider.get('getMegaTicketOwner', builder.build())).stack;
        const result = source.readAddressOpt();
        return result;
    }
    
}